/**
 * Daily Driver / Maintenance Brief — same visual pattern as Profit Monday Brief.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/apiClient';
import { formatDriverExamplesSummary } from '../../utils/formatDriverDisplayName';
import {
  STORED_VIEW_PREPARING_MESSAGE,
  preparingRetrySeconds,
  storedViewStaleMessage,
  type StoredViewProjection,
} from '../../utils/storedView';

export type DailySafetyBriefNavigate =
  | 'checks_missing'
  | 'checks_unverified'
  | 'checks_done'
  | 'checks_short'
  | 'checks_late'
  | 'checks_before'
  | 'compliance'
  | 'violations'
  | 'maintenance_defects'
  | 'maintenance_vor'
  | 'maintenance_schedule'
  | 'maintenance_faults';

export type DailyBriefScope = 'driver' | 'maintenance';

export interface DailySafetyBriefItem {
  id: string;
  priority: number;
  title: string;
  summary: string;
  action: string;
  category: 'checks' | 'defects' | 'vor' | 'compliance' | 'violations' | 'faults' | 'schedule';
  navigate?: DailySafetyBriefNavigate;
}

export interface DailySafetyBriefResponse {
  generated_at: string;
  /** Set when served from a stored view (the Driver tab's preset periods). */
  projection?: StoredViewProjection;
  date_from: string;
  date_to: string;
  scope?: DailyBriefScope;
  headline: string;
  items: DailySafetyBriefItem[];
  counts?: {
    open_defects?: number;
    in_progress?: number;
    fixed?: number;
    vor?: number;
    vor_vehicles?: string[];
    maintenance_overdue?: number;
    maintenance_due_today_tomorrow?: number;
    maintenance_due_in_2_to_7_days?: number;
    maintenance_unconfirmed?: number;
    active_faults?: number;
    missing_checks?: number;
    late_checks?: number;
    compliance_pct?: number;
    infringement_drivers?: number;
    driver_count?: number;
  };
}

interface DailySafetyBriefPanelProps {
  from: string;
  to: string;
  /** driver (default) = Daily Driver Brief; maintenance = Daily Maintenance Brief */
  scope?: DailyBriefScope;
  onNavigate?: (target: DailySafetyBriefNavigate) => void;
  /**
   * Changes whenever the parent re-reads its data for the same window (the
   * Driver tab's 15-minute poll, returning to the tab, Refresh), so the brief
   * follows newly imported data instead of only period changes. The current
   * brief stays on screen while it reloads.
   */
  refreshKey?: number;
}

function DailySafetyBriefSkeleton({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-border bg-card/40 p-4" aria-busy="true" aria-live="polite" aria-label={`Loading ${label}`}>
      <div className="h-3 w-40 rounded bg-secondary/40 animate-pulse mb-3" />
      <div className="space-y-2 mb-4">
        <div className="h-4 w-full max-w-lg rounded bg-secondary/40 animate-pulse" />
        <div className="h-4 w-full max-w-md rounded bg-secondary/35 animate-pulse" />
      </div>
      <ol className="space-y-4 list-none">
        {[0, 1, 2].map((i) => (
          <li key={i} className="space-y-2">
            <div className="flex gap-3">
              <div className="h-4 w-5 rounded bg-secondary/35 animate-pulse shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2 min-w-0">
                <div className="h-4 w-full rounded bg-secondary/45 animate-pulse" />
                <div className="h-3 w-full rounded bg-accent/40 animate-pulse" />
                <div className="h-3 w-full max-w-2xl rounded bg-success/25 animate-pulse" />
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function DailySafetyBriefPanel({
  from,
  to,
  scope = 'driver',
  onNavigate,
  refreshKey = 0,
}: DailySafetyBriefPanelProps) {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brief, setBrief] = useState<DailySafetyBriefResponse | null>(null);
  /** The stored brief for this period is still being built (202); a retry is scheduled. */
  const [preparing, setPreparing] = useState(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The newest request wins: a background reload for an old window must not
  // replace the brief of a period the user has since switched to.
  const requestSeq = useRef(0);

  const title = scope === 'maintenance' ? 'Daily Maintenance Brief' : 'Daily Driver Brief';
  const headerDescription =
    scope === 'maintenance'
      ? 'Defects & VOR'
      : 'Walkaround checks, compliance & violations';

  const load = useCallback(async () => {
    if (!from || !to) return;
    const seq = ++requestSeq.current;
    if (retryTimer.current) clearTimeout(retryTimer.current);
    retryTimer.current = null;
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(
        API_ENDPOINTS.fleetIntelligence.dailySafetyBrief({ from, to, scope }),
      );
      if (res.status === 202) {
        // Stored view still being built: ask again when it should be ready.
        const wait = await preparingRetrySeconds(res);
        if (seq !== requestSeq.current) return;
        setBrief(null);
        setPreparing(true);
        retryTimer.current = setTimeout(() => { void loadRef.current(); }, wait * 1000);
        return;
      }
      setPreparing(false);
      if (!res.ok) throw new Error(`Brief failed (${res.status})`);
      const json = (await res.json()) as DailySafetyBriefResponse;
      if (seq !== requestSeq.current) return;
      setBrief(json);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      // A source that could not be read fails the whole brief server-side, so
      // never leave an earlier brief's counts on screen beside the error.
      setBrief(null);
      const detail = err instanceof Error ? ` (${err.message})` : '';
      setError(`This brief couldn't be loaded, so no counts are shown — try again shortly.${detail}`);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [from, to, scope]);

  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
  }, []);

  const periodLabel = useMemo(() => {
    const a = String(from || '').slice(0, 10);
    const b = String(to || '').slice(0, 10);
    if (!a) return '';
    return !b || a === b ? a : `${a} → ${b}`;
  }, [from, to]);

  return (
    <section
      className="mb-4 bg-card border border-border rounded-xl overflow-hidden"
      aria-label={title}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border bg-card/60 text-left hover:bg-card/80 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
          )}
          <span className="text-base font-bold tracking-widest text-success uppercase">
            {title}
          </span>
          {periodLabel ? <span className="text-sm text-muted-foreground">{periodLabel}</span> : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-muted-foreground">{headerDescription}</span>
          <span
            role="button"
            tabIndex={0}
            title="Refresh brief"
            aria-label="Refresh brief"
            onClick={(e) => {
              e.stopPropagation();
              void load();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                void load();
              }
            }}
            className="p-1 rounded text-muted-foreground hover:text-white hover:bg-secondary/80"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden />
          </span>
        </div>
      </button>

      {open ? (
        <div key={String(open)} className="p-4 zoe-snap-in">
          {loading && !brief ? (
            <DailySafetyBriefSkeleton label={title} />
          ) : (
            <div className="rounded-lg border border-border bg-card/40 p-4">
              <div className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                {scope === 'maintenance' ? 'Priorities to act on' : "This period's focus"}
              </div>

              {error && <p className="text-sm text-destructive mb-3">{error}</p>}
              {preparing && <p className="text-sm text-ring mb-3">{STORED_VIEW_PREPARING_MESSAGE}</p>}
              {storedViewStaleMessage(brief?.projection) && (
                <p className="text-sm text-warning mb-3">{storedViewStaleMessage(brief?.projection)}</p>
              )}

              {brief && brief.items.length > 0 ? (
                <ol className="space-y-4 text-base text-foreground">
                  {brief.items.map((item, idx) => (
                    <li key={item.id} className="flex gap-3 leading-7">
                      <span className="shrink-0 w-6 text-right font-semibold text-muted-foreground tabular-nums">
                        {idx + 1}.
                      </span>
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-white text-base">{item.title}</span>
                        <div className={`text-sm text-muted-foreground mt-1 ${item.category === 'schedule' ? 'whitespace-pre-line' : ''}`}>
                          {item.category === 'schedule'
                            ? item.summary
                            : formatDriverExamplesSummary(
                              item.summary,
                              scope === 'maintenance' ? 'vehicles' : 'drivers',
                            )}
                        </div>
                        {item.navigate && onNavigate ? (
                          <button
                            type="button"
                            onClick={() => onNavigate(item.navigate!)}
                            className="block text-left text-sm text-success/90 mt-1.5 hover:text-success hover:underline"
                          >
                            {item.action || 'Open'}
                          </button>
                        ) : item.action ? (
                          <div className="text-sm text-success/90 mt-1.5">{item.action}</div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                !loading && !preparing && (
                  <p className="text-base text-muted-foreground leading-7">
                    No priority items for this window.
                  </p>
                )
              )}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
